import { parseDateInterval } from "./utilities/date";
import {
    defaultFaviconUrl,
    defaultPoliciesDefinition,
    defaultInsightsDefinition,
    defaultUserActivityDefinition,
    defaultAnalyticsDefinition,
    defaultLoginPageUrl,
    defaultQuotesConfig,
} from "@app/core/configuration";
import { SiteConfiguration } from "@dit/ui-configurations/configurations";
import { defaultLoginConfig } from "@app/components/layout/login/models";
import { defaultTrackerConfig } from "@app/modules/tracking/models";
import { defaultContactConfig } from "@app/modules/contact/contactModels";
import { defaultPolicySearchConfig } from "@app/modules/policySpace/policySearch/policySearchModels";
import { defaultDocumentAndFormsConfig } from "@app/modules/documents/documentSearchModel";
import { defaultCommissionConfig } from "@app/modules/commissions/constants";
import { defaultRecentNotificationsConfig } from "@app/modules/messageCenter/constants";
import { defaultPolicyDetailConfig } from "@app/modules/policySpace/common/policyDetailsModels";

export type GlobalConfigDefinition = {
    documentUpload: DocumentUploadConfigurationDefinition;
    addressComplete: AddressCompleteDefinition;
    userProfile: UserProfileDefinition;
    onBehalfOf: OnBehalfOfDefinition;
    signature: SignatureDefinition;
    externalLinksList: ExternalLinksListDefinition;
    documentsAndForms: DocumentsAndFormDefinition;
    footerLinks: FooterLinksDefinition;
    mainMenu: MainMenuDefinition;
    policyDocuments: PolicyDocumentsDefinition;
    pendingApplications: PendingApplicationsDefinition;
    policySearch: PolicySearchDefinition;
    policyDetail: PolicyDetialDefinition;
    tableHeader: TableHeaderDefinition;
    userRoleConfigurations: UserRoleConfigurationDefinition[];
    welcomeMessage: WelcomeMessageDefinition;
    headerLayout: HeaderLayoutDefinition;
    loginPage: LoginPageDefinition;
    homepage: HomepageDefinition;
    casesInAnalysis: CasesInAnalysis;
    tracker: TrackerDefinition;
    contact: ContactDefinition;
    idp: IdentityProviderDefinition;
    services: APIServiceDefinition;
    baseUrl: string;
    faviconUrl: string;
    loginPageUrl: string;
    i18n?: I18nDefinition;
    insights: InsightsDefinition;
    userActivity: UserActivityDefinition;
    policies: PoliciesDefinition;
    analytics: AnalyticsDefinition;
    commissions: CommissionssDefinition;
    recentNotifications: RecentNotificationsDefinition;
    quotes?: QuotesDefinition;
} & SiteConfiguration;

/**
 * Initializes your site configuration with the Mandatory values provided as the main parameter.
 * This method fills in the optional configurations with their respective default values.
 * @param baseConfiguration - the configuration with mandatory settings provided.
 * @returns {@link GlobalConfigDefinition} a merged site configuration between the provided baseConfiguration elements and the defaults configurations.
 */
export const createDefaultConfig = (baseConfiguration: GlobalConfigDefinition): GlobalConfigDefinition => {
    const fallbacks = {
        ...baseConfiguration,
        faviconUrl: baseConfiguration.faviconUrl ?? defaultFaviconUrl,
        loginPageUrl: baseConfiguration.loginPageUrl ?? defaultLoginPageUrl,
        analytics: baseConfiguration.analytics ?? defaultAnalyticsDefinition,
        policies: baseConfiguration.policies ?? defaultPoliciesDefinition,
        userActivity: baseConfiguration.userActivity ?? defaultUserActivityDefinition,
        insights: baseConfiguration.insights ?? defaultInsightsDefinition,
        loginPage: baseConfiguration.loginPage ?? defaultLoginConfig,
        tracker: baseConfiguration.tracker ?? defaultTrackerConfig,
        contact: baseConfiguration.contact ?? defaultContactConfig,
        policySearch: baseConfiguration.policySearch ?? defaultPolicySearchConfig,
        policyDetail: getPolicyDetailConfig(baseConfiguration?.policyDetail),
        documentsAndForms: baseConfiguration.documentsAndForms ?? defaultDocumentAndFormsConfig,
        commissions: baseConfiguration.commissions ?? defaultCommissionConfig,
        recentNotifications: baseConfiguration.recentNotifications ?? defaultRecentNotificationsConfig,
        quotes: baseConfiguration.quotes ?? defaultQuotesConfig,
    };

    return fallbacks;
};

const getPolicyDetailConfig = (policyDetailDefinition: PolicyDetialDefinition): PolicyDetialDefinition => {
    const defaultConfig: PolicyDetailConfigurations = {
        ...policyDetailDefinition?.default,
        actionButtons: {
            agent:
                policyDetailDefinition?.default?.actionButtons?.agent ??
                defaultPolicyDetailConfig?.actionButtons?.agent,
            client:
                policyDetailDefinition?.default?.actionButtons?.client ??
                defaultPolicyDetailConfig?.actionButtons?.client,
        },
    };

    return { ...policyDetailDefinition, default: defaultConfig };
};
export const getConfigPendingApplicationDate = (config: PendingApplicationsDefinition) => {
    const startDate = config?.startDate;
    const endDate = config?.endDate;

    return {
        startDate: startDate ? parseDateInterval(startDate) : new Date(Date.now()),
        endDate: endDate ? parseDateInterval(endDate) : new Date(Date.now()),
    };
};

export const getConfigHeaderLayout = (config: HeaderLayoutDefinition) => {
    const logoPosition = config?.logoPosition;
    return {
        logoPosition: logoPosition ?? "center",
    };
};

export const getPolicySearchTypesOfBusiness = (config: PolicySearchDefinition) => {
    return {
        hasMultipleType: config?.typesOfBusiness?.length != 1,
    };
};

export const getPolicySearchAdditionalFields = (config: PolicySearchDefinition) => {
    return {
        hasPFANumberField: config?.additionalFields?.includes("PFANumber"),
    };
};
